import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { UserService } from './user.service';
import { BehaviorSubject } from 'rxjs';

/**
 * Toggle Service
 * 
 * Usage example:
 * 
 * // In a component constructor
 * constructor(private toggleService: ToggleService) {}
 * 
 * // In ngOnInit or when needed
 * async ngOnInit() {
 *   // Load toggles first
 *   await this.toggleService.listToggles();
 *   
 *   // Check if a toggle is enabled for current user
 *   const isFeatureEnabled = this.toggleService.isToggleEnabled('new-feature');
 *   
 *   // Check if a toggle is enabled for a specific role
 *   const isAdminFeatureEnabled = this.toggleService.isToggleEnabled('admin-feature', 'super-admin');
 * }
 */

export interface Toggle {
  name: string;
  description?: string;
  enabled: boolean;
  access_type: 'technicians' | 'beta_users' | 'technicians_or_beta' | 'everyone';
}

@Injectable({
  providedIn: 'root'
})
export class ToggleService {
  private toggles: Toggle[] = [];
  public togglesLoaded = new BehaviorSubject<boolean>(false);
  public fetchingToggles = false;

  constructor(
    private http: HttpClient,
    private userService: UserService
  ) {}

  /**
   * Get all toggles from the API
   */
  async listToggles(): Promise<Toggle[]> {
    try {      
      if(!this.fetchingToggles) {
        this.fetchingToggles = true;
        let headers = this.headersWithToken();
        const response = await this.http.get(`${environment.claimApi}/toggles`, headers).toPromise();
        this.toggles = response as Toggle[];
        this.togglesLoaded.next(true);
        this.fetchingToggles = false;
        return this.toggles;
      } else {
        return this.toggles;
      }
    } catch (error) {
      console.error('Error fetching toggles:', error);
      throw error;
    }
  }

  /**
   * Role used for toggle access: the logged-in-as user when an admin is impersonating,
   * otherwise the current session user.
   */
  getEffectiveUserRole(): string {
    const loginData = this.userService.getLoginData();
    if (!loginData) {
      return 'user';
    }

    const sessionUser = this.userService.isLoggedInAsAnotherUser() && loginData.loggedInAs
      ? loginData.loggedInAs
      : loginData;

    if (sessionUser.beta_user) {
      return 'beta-user';
    }

    return sessionUser.role || 'user';
  }

  /**
   * Check if a toggle is enabled for a specific user role
   * @param toggleName - The name of the toggle to check
   * @param userRole - The user's role (e.g., 'super-admin', 'tech', 'premium-user', 'user')
   * @returns boolean indicating if the toggle is enabled for the user
   */
  isToggleEnabled(toggleName: string, userRole?: string): boolean { 
    // If toggles haven't been loaded yet, return false
    if (!this.togglesLoaded.getValue()) {
      this.listToggles();
      return false;
    }

    const toggle = this.toggles.find(t => t.name === toggleName);
    if (!toggle) {
      return false;
    }

    // If toggle is disabled, return false regardless of access type
    if (!toggle.enabled) {
      return false;
    }

    // If no user role provided, use the effective session user (supports admin login-as)
    if (!userRole) {
      userRole = this.getEffectiveUserRole();
    }

    return this.checkAccessType(toggle.access_type, userRole);
  }

  /**
   * Check if a user has access based on the toggle's access type
   * @param accessType - The toggle's access type
   * @param userRole - The user's role
   * @returns boolean indicating if the user has access
   */
  private checkAccessType(accessType: string, userRole: string): boolean {
    switch (accessType) {
      case 'everyone':
        return true;
      
      case 'technicians':
        return ['super-admin', 'tech'].includes(userRole);
      
      case 'beta_users':
        return ['super-admin', 'tech', 'beta-user'].includes(userRole);
      
      case 'technicians_or_beta':
        return ['super-admin', 'tech', 'beta-user'].includes(userRole);
      
      default:
        console.warn(`Unknown access type: ${accessType}`);
        return false;
    }
  }

  /**
   * Get all toggles (from cache if available)
   */
  getToggles(): Toggle[] {
    return [...this.toggles];
  }

  /**
   * Check if toggles have been loaded
   */
  areTogglesLoaded(): boolean {
    return this.togglesLoaded.getValue();
  }

  /**
   * Clear the toggle cache (useful for refreshing data)
   */
  clearCache(): void {
    this.toggles = [];
    this.togglesLoaded.next(false);
  }

  /**
   * Create a new toggle
   */
  async createToggle(toggleData: Partial<Toggle>): Promise<Toggle> {
    try {
      const response = await this.http.post(`${environment.claimApi}/toggles`, toggleData, this.headersWithToken()).toPromise();
      // Refresh the toggles list after creating
      await this.listToggles();
      return response as Toggle;
    } catch (error) {
      console.error('Error creating toggle:', error);
      throw error;
    }
  }

  /**
   * Update an existing toggle
   */
  async updateToggle(toggleName: string, toggleData: Partial<Toggle>): Promise<Toggle> {
    try {
      const response = await this.http.put(`${environment.claimApi}/toggles/${toggleName}`, toggleData, this.headersWithToken()).toPromise();
      // Refresh the toggles list after updating
      await this.listToggles();
      return response as Toggle;
    } catch (error) {
      console.error('Error updating toggle:', error);
      throw error;
    }
  }

  /**
   * Create headers with authentication token
   */
  private headersWithToken() {
    const loginData = this.userService.getLoginData();
    if (loginData) {
      return {
        headers: new HttpHeaders({
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'x-access-token': loginData.token
        })
      };
    }
    return {};
  }
} 